import React, { useRef, useEffect, useMemo } from "react";
import QRCodeStyling, { Options } from "qr-code-styling";
import clsx from "clsx";

import "./Qrcode.scss";
import { themeParams, useSignal } from "@telegram-apps/sdk-react";
import { config } from "~/config";

const IconQrLogo = `${config.cdn_host}/logo/uxuy-dex-logo.png`;

interface QrcodeProps {
    children?: React.ReactNode;
    className?: string;
    rerQrCodeClassName?: string;
    qrcodeValue: string;
    dotColor?: string;
    size?: "200px" | "160px";
}

export function Qrcode(props: QrcodeProps) {
    const isDark = useSignal(themeParams.isDark);
    const qrCodeRef = useRef<QRCodeStyling>();
    const rerQrCode = useRef(null);

    useEffect(() => {
        if (rerQrCode.current && !qrCodeRef.current) {
            const options: Options = {
                width: 1200,
                height: 1200,
                data: props.qrcodeValue,
                image: IconQrLogo,
                margin: 0,
                qrOptions: {
                    mode: "Byte",
                    errorCorrectionLevel: "H",
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.35,
                    margin: 20,
                },
                dotsOptions: {
                    type: "dots",
                    color: props.dotColor ?? "#000000",
                    gradient: undefined,
                },
                backgroundOptions: {
                    color: "transparent",
                    gradient: undefined,
                },
                cornersSquareOptions: {
                    type: "dot",
                    color: props.dotColor ?? "#000000",
                    gradient: undefined,
                },
                cornersDotOptions: {
                    type: "dot",
                    color: props.dotColor ?? "#000000",
                    gradient: undefined,
                },
            };

            qrCodeRef.current = new QRCodeStyling(options);
            qrCodeRef.current.append(rerQrCode.current);
        }
    }, [rerQrCode]);

    useEffect(() => {
        if (!props.qrcodeValue) return;
        const errorCorrectionLevel =
            props.qrcodeValue?.length > 100 ? "L" : "H";

        const qrcodeThemeColor = isDark ? "#ffffff" : "#000000";
        const qrcodeRenderColor = props.dotColor ?? qrcodeThemeColor;

        const options: Options = {
            width: 1200,
            height: 1200,
            data: props.qrcodeValue,
            image: IconQrLogo,
            margin: 0,
            qrOptions: {
                mode: "Byte",
                errorCorrectionLevel: errorCorrectionLevel,
            },
            imageOptions: {
                hideBackgroundDots: true,
                imageSize: errorCorrectionLevel === "L" ? 0.8 : 0.32,
                margin: errorCorrectionLevel === "L" ? 20 : 20,
            },
            dotsOptions: {
                type: "dots",
                color: qrcodeRenderColor,
                gradient: undefined,
            },
            backgroundOptions: { color: "transparent", gradient: undefined },
            cornersSquareOptions: {
                type: "dot",
                color: qrcodeRenderColor,
                gradient: undefined,
            },
            cornersDotOptions: {
                type: "dot",
                color: qrcodeRenderColor,
                gradient: undefined,
            },
        };

        qrCodeRef.current?.update?.(options);
    }, [isDark, props.qrcodeValue, props.dotColor]);

    const size = useMemo(() => {
        switch (props.size) {
            case "160px":
                return "w-[160px] h-[160px]";
            default:
                return "w-[200px] h-[200px]";
        }
    }, [props.size]);

    return (
        <>
            <div
                className={clsx(
                    "flex items-center flex-col justify-center",
                    props.className
                )}
            >
                <div
                    ref={rerQrCode}
                    className={clsx(
                        "qrcode_wrapper rounded-none",
                        size,
                        props.rerQrCodeClassName || ""
                    )}
                ></div>
            </div>
        </>
    );
}
